// A file containing a list of reusable functions that are in no way assosiated
import * as LocalStorage from "../enums/LocalStorageValues";
import { BASE_URL, REFRESH } from "../enums/ApiPaths";
import axios, { HttpStatusCode } from "axios";
import dayjs from "dayjs";
import { useMediaQuery } from "@mui/material";
import { useTheme } from "@emotion/react";
import { fileToBase64 } from "../components/form/fileUpload/FileUploadEditingForm";
import { PROFILE_EDIT } from "../enums/UrlPaths";
import logger from "./logger";

export function logout() {
  const lastUrl = getLastVisitedUrl();
  const theme = localStorage.getItem("theme");
  const lang = localStorage.getItem("lang");
  const calendarView = localStorage.getItem(LocalStorage.CALENDAR_VIEW);
  localStorage.removeItem(LocalStorage.TOKEN);
  localStorage.removeItem(LocalStorage.REFRESH_TOKEN);
  localStorage.removeItem(LocalStorage.REFRESH_TOKEN_EXPIRATION);
  localStorage.removeItem(LocalStorage.NEED_TO_CHANGE_PASSWORD);
  localStorage.removeItem(LocalStorage.SUBSCRIBE_TO_EMAILS);
  localStorage.removeItem(LocalStorage.GOOGLE_ID);
  localStorage.removeItem(LocalStorage.IS_PASSWORD_EMPTY);
  localStorage.removeItem(LocalStorage.BUSINESS);
  if (lastUrl) setLastVisitedUrl(lastUrl);
  if (theme) localStorage.setItem("theme", theme);
  if (lang) localStorage.setItem("lang", lang);
  if (calendarView) localStorage.setItem(LocalStorage.CALENDAR_VIEW, calendarView);
}

export function isLast(array, index) {
  if (Array.isArray(array)) {
    return Number(index) < array.length - 1;
  }
}

export function refresh() {
  window.location.reload();
}

export function isArrayEmpty(data) {
  if (isArray(data)) {
    return data?.length == 0;
  }
  return false;
}

export function isArray(data) {
  return Array.isArray(data);
}

export async function getPrepredFiles(files = []) {
  const submitData = await Promise.all(
    files?.map(async (fileData, index) => {
      const file = files[index];
      if (file) {
        var base64File = await fileToBase64(file);
        const fileExtension = file.name.split(".").pop();
        const inputedTitle =
          fileData?.title?.split(".").slice(0, -1).join(".") || fileData?.title;
        const fileTitleWithExtension = fileData?.title
          ? `${inputedTitle}.${fileExtension}`
          : file.name;
        return {
          file: base64File,
          fileTitle: fileTitleWithExtension,
          documentCategory: fileData?.category ? fileData?.category : null,
        };
      }
    }),
  );

  const filteredData = submitData.filter(
    (item) => item !== undefined && item !== null,
  );

  return filteredData;
}

// The backend rotates refresh tokens: each successful POST /auth/refresh
// deletes the token it was sent. Two refreshes sent with the same token (two
// callers in one tab, or two tabs sharing localStorage) therefore mean one
// of them gets a 401 - which used to leave that caller's request without an
// Authorization header, answered by a 401 that logged the user out and wiped
// the valid tokens the other refresh had just stored. So every refresh goes
// through here: one shared promise per tab, serialized across tabs with a
// Web Lock.
const REFRESH_LOCK = "zookeeper-token-refresh";
let pendingRefresh = null;

export function refreshTokens() {
  if (!pendingRefresh) {
    pendingRefresh = refreshTokensAcrossTabs().finally(() => {
      pendingRefresh = null;
    });
  }
  return pendingRefresh;
}

async function refreshTokensAcrossTabs() {
  const seenRefreshToken = getRefreshToken();
  const run = () => {
    // Another tab refreshed while we waited for the lock - its tokens are
    // already in localStorage, and ours has been rotated away on the backend.
    if (getRefreshToken() !== seenRefreshToken) {
      return isTokenValid() ? { status: HttpStatusCode.Ok } : null;
    }
    return postRefreshTokens();
  };
  if (navigator.locks) {
    return navigator.locks.request(REFRESH_LOCK, run);
  }
  return run();
}

async function postRefreshTokens() {
  // NOTE: intentionally NOT using the `api` (authApi.js) instance here.
  // authApi.js's request interceptor calls getToken() from this same file,
  // which would create a circular import (Functions.jsx -> authApi.js -> Functions.jsx).
  // This call also generates the token in the first place, so it must not
  // depend on an interceptor that reads the (possibly stale/expired) token.
  // Keeping it on plain axios avoids the circular dependency entirely.
  const url = BASE_URL + REFRESH;
  const token = { refreshToken: getRefreshToken() };
  try {
    const res = await axios.post(url, token);
    if (res.status === HttpStatusCode.Ok) {
      updateTokens(res.data);
    }
    return res;
  } catch (error) {
    logger.error("Token refresh failed:", error);
    return null;
  }
}

export function getTokenFromUrl() {
  const urlSearchString = window.location.search;
  const params = new URLSearchParams(urlSearchString);
  return params.get("token");
}

export function getMailFromUrl() {
  const urlSearchString = window.location.search;
  const params = new URLSearchParams(urlSearchString);
  return params.get("mail");
}

export function isTokenValid() {
  const decodedJwt = parseJwt(getToken());
  // Treat the token as expired 30 seconds early so we always refresh before
  // it actually expires on the server, preventing near-expiry 401 responses.
  return decodedJwt?.exp * 1000 > Date.now() + 30_000;
}

export function parseJwt(token) {
  try {
    return JSON.parse(atob(token.split(".")[1]));
  } catch (e) {
    return null;
  }
}

export function isRefreshTokenValid() {
  const refreshExpiration = dayjs(
    localStorage.getItem(LocalStorage.REFRESH_TOKEN_EXPIRATION),
    "DD/MM/YYYY, HH:mm:ss",
  );
  return dayjs().isBefore(refreshExpiration);
}

export function removeLastUrl() {
  localStorage.removeItem(LocalStorage.LAST_VISITED_URL);
}

export function getIsBusinessAccount() {
  return localStorage.getItem(LocalStorage.BUSINESS) === "true";
}

//Remove this later once all get token functions have been replaced with fetching from query
//TODO: somethings need to be left in localStorage (like tokens)
export function setLocalStorageData(res) {
  localStorage.setItem(LocalStorage.TOKEN, res?.token);
  localStorage.setItem(LocalStorage.REFRESH_TOKEN, res?.refreshToken.token);
  localStorage.setItem(
    LocalStorage.REFRESH_TOKEN_EXPIRATION,
    res?.refreshToken.expiration,
  );

  //Other things need to be adapted, we should look into removing the login dto and replacing it with the profile dto completely
  // Simply mergin the login dto into the profile dto
  // Need a ticket for this
  // Possibly even moving the tokens into the dto, need to try this, not sure if it will work
  localStorage.setItem(
    LocalStorage.SUBSCRIBE_TO_EMAILS,
    JSON.stringify(res?.subscribeToEmails),
  );

  localStorage.setItem(
    LocalStorage.NEED_TO_CHANGE_PASSWORD,
    JSON.stringify(res?.needToChangePassword),
  );

  localStorage.setItem(LocalStorage.IS_PASSWORD_EMPTY, res?.passwordEmpty);

  localStorage.setItem(LocalStorage.GOOGLE_ID, JSON.stringify(res?.googleId));
}

export function getGoogleId() {
  return JSON.parse(localStorage.getItem(LocalStorage.GOOGLE_ID));
}

export function getSubsribesToEmail() {
  return JSON.parse(localStorage.getItem(LocalStorage.SUBSCRIBE_TO_EMAILS));
}

export function setNeedToChangePassword() {
  return localStorage.setItem(LocalStorage.NEED_TO_CHANGE_PASSWORD, false);
}

export function getNeedToChangePassword() {
  return JSON.parse(localStorage.getItem(LocalStorage.NEED_TO_CHANGE_PASSWORD));
}

export function getUserAddress() {
  return localStorage.getItem(LocalStorage.ADDRESS);
}
export function getClinicName() {
  return localStorage.getItem(LocalStorage.CLINIC_NAME);
}

export function getClinicShortName() {
  return localStorage.getItem(LocalStorage.CLINIC_SHORT_NAME);
}

export function isSubscribedToEmails() {
  return localStorage.getItem(LocalStorage.SUBSCRIBE_TO_EMAILS);
}

export function isPasswordEmpty() {
  return localStorage.getItem(LocalStorage.IS_PASSWORD_EMPTY);
}

export function setRefreshToken(data) {
  localStorage.setItem(LocalStorage.REFRESH_TOKEN, data.refreshToken);
}

export function setAuthToken(data) {
  localStorage.setItem(LocalStorage.TOKEN, data.token);
}

export function updateTokens(data) {
  setRefreshToken(data);
  setAuthToken(data);
}

export function setPasswordEmpty(data) {
  localStorage.setItem(LocalStorage.IS_PASSWORD_EMPTY, data);
}

export function isNullOrEmpty(string) {
  const checkVal = String(string);
  return checkVal.length == 0 || checkVal == null;
}

export function getYYYYmmDD(input_date) {
  try {
    const date = new Date(input_date);
    var mm;
    var dd;
    var fulldate;
    mm = date.getMonth() + 1; // getMonth() is zero-based
    dd = date.getDate();
    fulldate = [
      (dd > 9 ? "" : "0") + dd,
      (mm > 9 ? "" : "0") + mm,
      date.getFullYear(),
    ].join("/");
    return fulldate;
  } catch (err) {
    throw new Error("Molimo Vas izaberite validan datum!");
  }
}

export function getHHmmss(input_date) {
  try {
    const d = new Date(input_date);
    let dTime;
    dTime = [
      addLeadingZero(d.getHours()),
      addLeadingZero(d.getMinutes()),
      addLeadingZero(d.getSeconds()),
    ].join(":");
    return dTime;
  } catch (err) {
    throw new Error("Molimo Vas izaberite vrijeme dolaska!");
  }
}

export function getYYYYmmDDHHmm(input_date) {
  try {
    var d = new Date(input_date);
    let dDate;
    let dTime;
    dDate = [
      addLeadingZero(d.getDate()),
      addLeadingZero(d.getMonth() + 1),
      d.getFullYear(),
    ].join("/");
    dTime = [
      addLeadingZero(d.getHours()),
      addLeadingZero(d.getMinutes()),
      addLeadingZero(d.getSeconds()),
    ].join(":");
    return dDate + ", " + dTime;
  } catch (err) {
    throw new Error("Molimo Vas izaberite validan datum!");
  }
}

export function isEmpty(array) {
  return array.length == 0 ? true : false;
}

export function addLeadingZero(string) {
  return string < 10 ? "0" + string : string;
}

export function getLastPetViewIndex() {
  return Number(localStorage.getItem(LocalStorage.LAST_PET_INDEX)) || 0;
}

export function setLastPetIndex(index) {
  localStorage.setItem(LocalStorage.LAST_PET_INDEX, Number(index));
}

export function getLastAppointmentIndex() {
  return Number(localStorage.getItem(LocalStorage.LAST_APPOINTMENT_INDEX)) || 0;
}

export function setLastAppointmentIndex(index) {
  localStorage.setItem(LocalStorage.LAST_APPOINTMENT_INDEX, Number(index));
}

export function getToken() {
  return localStorage.getItem(LocalStorage.TOKEN);
}

export function setSuscribedToEmails(subscribe) {
  localStorage.setItem(LocalStorage.SUBSCRIBE_TO_EMAILS, subscribe);
}

// Stores the page the user was on when their session ended, so login can return them to it.
// Used to store the previously saved "current" URL instead, which was never cleared on login -
// login then restored a stale page from an earlier session (often the profile page).
export function setLastVisitedUrl(url) {
  localStorage.setItem(LocalStorage.LAST_VISITED_URL, url);
}

export function getLastVisitedUrl() {
  return localStorage.getItem(LocalStorage.LAST_VISITED_URL);
}

export function getRefreshToken() {
  return localStorage.getItem(LocalStorage.REFRESH_TOKEN);
}

export async function getBearerToken() {
  if (isRefreshTokenValid()) {
    if (!isTokenValid()) {
      // refreshTokens() already shares one in-flight request per tab.
      const res = await refreshTokens();
      if (!res || res.status !== 200) {
        return null;
      }
    }

    return { Authorization: `Bearer ${getToken()}` };
  }
  return null;
}

export function getAuthorization() {
  const token = getToken();
  return { Authorization: `Bearer ${token}` };
}

export function getTodaysDayAsNumber() {
  var today = new Date();
  return String(today.getDate()).padStart(2);
}

export function getTodaysDayAsString() {
  return new Date().toLocaleString("cro", { weekday: "long" });
}

export function getTomorrowsDayAsString() {
  return new Date(new Date().getTime() + 24 * 60 * 60 * 1000).toLocaleString(
    "cro",
    { weekday: "long" },
  );
}

export function capitalizeFirstLetter(string) {
  if (string) {
    return string.charAt(0).toUpperCase() + string.slice(1).toLowerCase();
  }
}

export function getTomorrowsDayAsNumber() {
  return String(
    new Date(new Date().getTime() + 24 * 60 * 60 * 1000).getDate(),
  ).padStart(2);
}

export function getTodaysDate() {
  var today = new Date();
  var dd = String(today.getDate()).padStart(2, "0");
  var mm = String(today.getMonth() + 1).padStart(2, "0"); //January is 0!
  var yyyy = today.getFullYear();
  var fullString = dd + "/" + mm + "/" + yyyy;
  return fullString;
}

//Idk if this is used anymore, caused issues
export function formatDate(dateString) {
  //returns dd/MM/yyyy format from
  var parts = dateString.split("/");
  return new Date(parts[2], parts[1] - 1, parts[0]);
}

export function getDateFromString(dateString) {
  //Works only for dd/MM/yyyy string format
  var parts = dateString.split("/");
  return new Date(parts[0], parts[1] - 1, parts[2]);
}

export function getDateFromDateAndTimeString(dateTimeString) {
  //retuns dd/mm/yyyy from dd/mm/yyyy HH:mm
  return String(dateTimeString).substring(0, 10);
}

export function getUsDateFormat(dateString) {
  var parse = dateString.split("/");
  return parse[1] + "/" + parse[0] + "/" + parse[2];
}

export function getTimeFromDateAndTimeString(dateTimeString) {
  //retuns HH:mm from dd/mm/yyyy HH:mm
  return dateTimeString.substring(11, 17);
}

export function getAppointementDateTime(dateTimeString) {
  var date = getUsDateFormat(dayjs(dateTimeString).format("DD/MM/YYYY"));
  var time = dayjs(dateTimeString).format("HH:mm");
  return new Date(date + ", " + time);
}

export function getDateOfAppointement(date) {
  return getDateFromDateAndTimeString(date);
}

export function removeLastChar(string) {
  return string.substring(0, string.length - 1);
}

export function removeTrailingSlash(path) {
  return path.endsWith("/") && path.length > 1 ? path.slice(0, -1) : path;
}

export function setDefaultValues(values, setValue) {
  if (values) {
    Object.keys(values).forEach((key) => {
      setValue(key, values[key]);
    });
  }
}

export async function isLocation(location) {
  return String(window.location.pathname) === location;
}

export function useIsMobile() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("sm"));
}
/**
 * @name useIsDesktop
 * @description Checks if the current screen size is medium ('md') or larger.
 * (e.g., typically Desktop or large tablet, >= 960px).
 * @returns {boolean} True if the screen width is >= theme.breakpoints.up("md").
 */
export function useIsDesktop() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.up("md"));
}

export function useIsTablet() {
  const theme = useTheme();
  return useMediaQuery(theme.breakpoints.down("lg"));
}

export function useIsSmallerScreen() {
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  return isMobile || isTablet;
}

export const trimText = (value, trimLength = 20) => {
  if (value != undefined && typeof value == "string") {
    const segmenter = new Intl.Segmenter(undefined, {
      granularity: "grapheme",
    });
    const graphemes = Array.from(segmenter.segment(value), (s) => s.segment);
    if (graphemes.length > trimLength) {
      return graphemes.slice(0, trimLength).join("") + "...";
    }
  }
  return value;
};

export function areArraysTheSame(arr1, arr2) {
  if (Number(arr1.length) !== Number(arr2.length)) return false;

  arr1.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
  arr2.sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

  for (let i = 0; i < arr1.length; i++) {
    if (JSON.stringify(arr1[i]) !== JSON.stringify(arr2[i])) {
      return false;
    }
  }

  return true;
}

export function replaceEmptyStringsWithNull(obj) {
  for (let key in obj) {
    if (obj[key] === "") {
      obj[key] = null;
    } else if (typeof obj[key] === "object" && obj[key] !== null) {
      replaceEmptyStringsWithNull(obj[key]);
    }
  }
  return obj;
}

export function isObjectEmpty(obj) {
  for (const prop in obj) {
    if (Object.hasOwn(obj, prop)) {
      return false;
    }
  }

  return true;
}

export function mandatory(string = " ") {
  if (string.length == 0) return;
  return `${string} *`;
}

export function saveBoxNavigationId(id) {
  saveToSessionStorage("boxIdNavigation", id);
}

export function saveToSessionStorage(key, value) {
  sessionStorage.setItem(key, value);
}

export function getBoxNavigationId() {
  return sessionStorage.getItem("boxIdNavigation");
}


export function isEmptyString(string) {
  if (!string) return true;
  return String(string).trim() === "";
}

export function navigateToPhoneSetup(navigate) {
  navigate(PROFILE_EDIT, {
    state: {
      missingPhoneNumber: true,
      forceTab: 0,
      timestamp: Date.now(),
    },
    replace: false,
  });
}
